import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  from,
  type NormalizedCacheObject,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { setContext } from "@apollo/client/link/context";
import { appConfig } from "../config/app-config";
import { loadSession } from "../auth/session";

export type GraphQLErrorHandler = (message: string) => void;

let errorHandler: GraphQLErrorHandler | null = null;

export function setGraphQLErrorHandler(handler: GraphQLErrorHandler | null): void {
  errorHandler = handler;
}

function createAuthLink(): ApolloLink {
  return setContext((_, { headers }) => {
    const session = loadSession();
    return {
      headers: {
        ...headers,
        ...(session?.accessToken ? { Authorization: `Bearer ${session.accessToken}` } : {}),
      },
    };
  });
}

function createErrorLink(): ApolloLink {
  return onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        errorHandler?.(err.message);
      }
    }
    if (networkError) {
      errorHandler?.(networkError.message);
    }
  });
}

export function createApolloClient(): ApolloClient<NormalizedCacheObject> {
  const httpLink = new HttpLink({
    uri: appConfig.graphqlUrl,
    fetchOptions: {
      // AbortSignal timeout when supported by the runtime
    },
  });

  return new ApolloClient({
    link: from([createErrorLink(), createAuthLink(), httpLink]),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "cache-and-network",
        errorPolicy: "all",
      },
      query: {
        fetchPolicy: "network-only",
        errorPolicy: "all",
      },
      mutate: {
        errorPolicy: "all",
      },
    },
    devtools: {
      enabled: appConfig.environment === "development",
    },
  });
}
